import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, ClockIcon, LayoutListIcon, EyeIcon } from 'lucide-react';
import { DataTable, Column } from '../components/DataTable';
import { mockStockEntries } from '../data/mockData';
import { StockEntry } from '../types';
type TabFilter = 'all' | 'Stock In' | 'Stock Out';
export function StockHistory() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  // Calculate summary stats
  const totalStockIn = mockStockEntries.filter(e => e.type === 'Stock In').reduce((sum, e) => sum + e.quantity, 0);
  const totalStockOut = mockStockEntries.filter(e => e.type === 'Stock Out').reduce((sum, e) => sum + e.quantity, 0);
  const totalTransactions = mockStockEntries.length;
  const pendingCount = mockStockEntries.filter(e => e.status === 'Pending').length;
  // Filter data based on active tab
  const filteredData = activeTab === 'all' ? mockStockEntries : mockStockEntries.filter(e => e.type === activeTab);
  const columns: Column<StockEntry>[] = [{
    key: 'transactionNo',
    label: 'Transaction No',
    render: item => <span className="font-medium text-indigo-600" data-id="element-2393">
          {item.transactionNo}
        </span>
  }, {
    key: 'type',
    label: 'Type',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.type === 'Stock In' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2394">
          {item.type === 'Stock In' ? <ArrowDownIcon className="h-3 w-3 mr-1" data-id="element-2395" /> : <ArrowUpIcon className="h-3 w-3 mr-1" data-id="element-2396" />}
          {item.type === 'Stock In' ? 'Gain' : 'Loss'}
        </span>
  }, {
    key: 'date',
    label: 'Date'
  }, {
    key: 'product',
    label: 'Product'
  }, {
    key: 'productCode',
    label: 'Product Code'
  }, {
    key: 'warehouse',
    label: 'Warehouse'
  }, {
    key: 'quantity',
    label: 'Qty',
    render: item => <span className={`font-medium ${item.type === 'Stock In' ? 'text-green-600' : 'text-red-600'}`} data-id="element-2397">
          {item.type === 'Stock In' ? '+' : '-'}
          {item.quantity}
        </span>
  }, {
    key: 'unitOfMeasure',
    label: 'UoM'
  }, {
    key: 'referenceType',
    label: 'Ref Type'
  }, {
    key: 'referenceNo',
    label: 'Ref No'
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'Completed' ? 'bg-green-100 text-green-800' : item.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2398">
          {item.status}
        </span>
  }];
  const tabs: {
    key: TabFilter;
    label: string;
  }[] = [{
    key: 'all',
    label: 'All Transactions'
  }, {
    key: 'Stock In',
    label: 'Gain'
  }, {
    key: 'Stock Out',
    label: 'Loss'
  }];
  return <div className="space-y-6" data-id="element-2399">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2400">
        <div data-id="element-2401">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2402">Stock History</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2403">
            Track all historical stock movements — incoming and outgoing
            inventory transactions.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2404">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2405">
          <div className="flex items-center" data-id="element-2406">
            <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg" data-id="element-2407">
              <ArrowDownIcon className="h-6 w-6 text-green-600" data-id="element-2408" />
            </div>
            <div className="ml-4" data-id="element-2409">
              <p className="text-sm font-medium text-gray-500" data-id="element-2410">Total Gain</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-2411">
                +{totalStockIn.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2412">
          <div className="flex items-center" data-id="element-2413">
            <div className="flex-shrink-0 p-3 bg-red-100 rounded-lg" data-id="element-2414">
              <ArrowUpIcon className="h-6 w-6 text-red-600" data-id="element-2415" />
            </div>
            <div className="ml-4" data-id="element-2416">
              <p className="text-sm font-medium text-gray-500" data-id="element-2417">Total Loss</p>
              <p className="text-2xl font-bold text-red-600" data-id="element-2418">
                -{totalStockOut.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2419">
          <div className="flex items-center" data-id="element-2420">
            <div className="flex-shrink-0 p-3 bg-indigo-100 rounded-lg" data-id="element-2421">
              <LayoutListIcon className="h-6 w-6 text-indigo-600" data-id="element-2422" />
            </div>
            <div className="ml-4" data-id="element-2423">
              <p className="text-sm font-medium text-gray-500" data-id="element-2424">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2425">
                {totalTransactions}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2426">
          <div className="flex items-center" data-id="element-2427">
            <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-lg" data-id="element-2428">
              <ClockIcon className="h-6 w-6 text-yellow-600" data-id="element-2429" />
            </div>
            <div className="ml-4" data-id="element-2430">
              <p className="text-sm font-medium text-gray-500" data-id="element-2431">Pending</p>
              <p className="text-2xl font-bold text-yellow-600" data-id="element-2432">
                {pendingCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Filters */}
      <div className="border-b border-gray-200" data-id="element-2433">
        <nav className="-mb-px flex space-x-8" data-id="element-2434">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.key ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2435">
              {tab.label}
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-2436">
                {tab.key === 'all' ? mockStockEntries.length : mockStockEntries.filter(e => e.type === tab.key).length}
              </span>
            </button>)}
        </nav>
      </div>

      {/* Data Table */}
      <div className="h-[500px]" data-id="element-2437">
        <DataTable columns={columns} data={filteredData} keyExtractor={item => item.id} searchPlaceholder="Search transactions..." actions={item => <div className="flex items-center gap-2 justify-end" data-id="element-2439">
              <button onClick={() => alert(`View details for ${item.transactionNo}`)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-2440">
                <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-2441" /> View
              </button>
            </div>} data-id="element-2438" />
      </div>
    </div>;
}